import { Optional } from '@/generic/functional';
import { Pagination } from '@/generic/pagination/domain/Pagination.ts';

type PageBuilder<Content> = {
  content: Content[];
  currentPage: number;
  pageSize: number;
  totalElementsCount: number;
};

const assertNotLowerThan = (subject: string, value: number, minimum: number) => {
  if (value < minimum) {
    throw new Error(`${subject} '${value}' is lower than ${minimum}`);
  }
};

export class Page<Content> {
  readonly #content: Content[];
  readonly #currentPage: number;
  readonly #pageSize: number;
  readonly #totalElementsCount: number;

  private constructor(builder: PageBuilder<Content>) {
    assertNotLowerThan('Current page', builder.currentPage, 1);
    assertNotLowerThan('Page size', builder.pageSize, 1);
    assertNotLowerThan('Total elements count', builder.totalElementsCount, 0);
    this.#content = builder.content;
    this.#currentPage = builder.currentPage;
    this.#pageSize = builder.pageSize;
    this.#totalElementsCount = builder.totalElementsCount;
  }

  static of<Content>(builder: PageBuilder<Content>): Page<Content> {
    return new Page(builder);
  }

  get content(): Content[] {
    return this.#content;
  }

  get currentPage(): number {
    return this.#currentPage;
  }

  get pageSize(): number {
    return this.#pageSize;
  }

  get totalElementsCount(): number {
    return this.#totalElementsCount;
  }

  get totalPages(): number {
    return Math.ceil(this.#totalElementsCount / this.#pageSize);
  }

  get next(): Optional<Pagination> {
    if (this.#currentPage >= this.totalPages) {
      return Optional.empty();
    }
    return Optional.of(Pagination.of({ page: this.#currentPage + 1, pageSize: this.#pageSize }));
  }
}
